import React, {
  useMemo
} from "react";

import {
  Link,
  useParams,
  useSearchParams
} from "react-router-dom";

import {
  Alert,
  Button,
  Content,
  PageSection,
  Stack,
  StackItem
} from "@patternfly/react-core";

import EducationDirectionPageSkeleton
  from "../components/EducationDirectionPageSkeleton";

import EducationProfessionSelect
  from "../components/EducationProfessionSelect";

import EducationDirectionHero
  from "../components/EducationDirectionHero";

import EducationDirectionNavigation
  from "../components/EducationDirectionNavigation";

import EducationDirectionQualifications
  from "../components/EducationDirectionQualifications";

import EducationDirectionSkills
  from "../components/EducationDirectionSkills";

import EducationDirectionProfession
  from "../components/EducationDirectionProfession";

import EducationDirectionCareers
  from "../components/EducationDirectionCareers";

import EducationDirectionSubjects
  from "../components/EducationDirectionSubjects";

import EducationDirectionLearningPath
  from "../components/EducationDirectionLearningPath";

import EducationDirectionFurtherEducation
  from "../components/EducationDirectionFurtherEducation";

import EducationDirectionCallToAction
  from "../components/EducationDirectionCallToAction";

import useEducationDirections from "../hooks/useEducationDirections";

const EducationDirectionNotFound = ({
  branchSymbol
}) => {
  return (
    <PageSection isWidthLimited>
      <Stack hasGutter>
        <StackItem>
          <Content>
            <h1>Nie znaleziono kierunku</h1>

            <p>
              {branchSymbol
                ? (
                  <>
                    Kierunek o symbolu{" "}
                    <strong>
                      {branchSymbol.toUpperCase()}
                    </strong>{" "}
                    nie istnieje lub nie jest obecnie dostępny.
                  </>
                )
                : (
                  <>
                    Nie podano symbolu kierunku kształcenia.
                  </>
                )}
            </p>
          </Content>
        </StackItem>

        <StackItem>
          <Button
            component={Link}
            to="/candidate"
            variant="primary"
          >
            Informacje dla kandydatów
          </Button>
        </StackItem>
      </Stack>
    </PageSection>
  );
};

const LIMITED_DIRECTION_IDS = new Set([
  "special-vocational",
  "multi-profession",
  "second-degree",
  "adult-school"
]);

const EducationDirectionPage = () => {
  const {
    getDirection,
    isLoading,
    error
  } = useEducationDirections();
  const {
    branchSymbol
  } = useParams();

  const normalizedBranchSymbol =
    branchSymbol?.trim().toLowerCase();

  // const direction = educationDirections[normalizedBranchSymbol];
  const direction = getDirection(normalizedBranchSymbol);

  const professions =
    direction?.professions ?? [];

  const [
    searchParams,
    setSearchParams
  ] = useSearchParams();

  const requestedProfessionId =
    searchParams.get("profession");

  const selectedProfession = useMemo(() => {
    return professions.find(
      (profession) =>
        profession.id === requestedProfessionId
    ) ?? null;
  }, [
    professions,
    requestedProfessionId
  ]);

  const selectedProfessionId =
    selectedProfession?.id ?? null;
  
  const handleProfessionChange = (
    professionId
  ) => {
    const nextSearchParams =
      new URLSearchParams(searchParams);

    if (professionId) {
      nextSearchParams.set(
        "profession",
        professionId
      );
    } else {
      nextSearchParams.delete("profession");
    }

    setSearchParams(
      nextSearchParams,
      {
        replace: true
      }
    );
  };


  const displayedDirection = useMemo(() => {
    if (!direction || !selectedProfession) {
      return direction;
    }

    return {
      ...direction,
      professionNumber:
        selectedProfession.professionNumber
    };
  }, [
    direction,
    selectedProfession
  ]);

  const directionImages = useMemo(() => {
    const images =
      selectedProfession?.hero?.images ??
      direction?.hero?.images ??
      [];

    if (!images.length) {
      return {
        heroImage: null,
        callToActionImage: null
      };
    }

    if (images.length === 1) {
      return {
        heroImage: images[0],
        callToActionImage: images[0]
      };
    }

    const heroImageIndex =
      Math.floor(Math.random() * images.length);

    const callToActionImageIndex =
      (heroImageIndex + 1) % images.length;

    return {
      heroImage: images[heroImageIndex],
      callToActionImage:
        images[callToActionImageIndex]
    };
  }, [
    direction,
    selectedProfession
  ]);

  const isLimitedDirection =
  direction
    ? LIMITED_DIRECTION_IDS.has(direction.id)
    : false;

  if (isLoading) {
    return (
      <EducationDirectionPageSkeleton />
    );
  }

  if (error) {
    return (
      <PageSection isWidthLimited>
        <Alert
          variant="danger"
          title="Nie udało się pobrać danych kierunku"
        >
          {error.message}
        </Alert>
      </PageSection>
    );
  }

  if (!direction) {
    return (
      <EducationDirectionNotFound
        branchSymbol={branchSymbol}
      />
    );
  }

  return (
    <Stack hasGutter>
      <StackItem>
        <EducationDirectionHero
          direction={displayedDirection}
          image={directionImages.heroImage}
          professionSelector={
            professions.length > 0 ? (
              <EducationProfessionSelect
                professions={professions}
                selectedProfessionId={
                  selectedProfessionId
                }
                onProfessionChange={
                  handleProfessionChange
                }
              />
            ) : null
          }
        />
      </StackItem>

      {!isLimitedDirection && (
        <>
          <StackItem isFilled />

          <StackItem>
            <EducationDirectionQualifications
              qualifications={direction.qualifications}
            />
          </StackItem>

          <StackItem isFilled />

          <StackItem>
            <EducationDirectionSkills
              skills={direction.skills}
            />
          </StackItem>

          <StackItem isFilled />

          <StackItem>
            <EducationDirectionSubjects
              subjectGroups={direction.subjectGroups}
            />
          </StackItem>

          <StackItem isFilled />

          <StackItem>
            <EducationDirectionLearningPath
              learningPath={direction.learningPath}
            />
          </StackItem>

          <StackItem isFilled />

          <StackItem>
            <EducationDirectionCareers
              careers={direction.careers}
            />
          </StackItem>

          <StackItem isFilled />
        </>
      )}

      <StackItem>
        <EducationDirectionCallToAction
          callToAction={
            selectedProfession?.callToAction ??
            direction.callToAction
          }
          image={directionImages.callToActionImage}
        />
      </StackItem>
    </Stack>
  );
};


export default EducationDirectionPage;

